// src/ModuloCadastro.js
import React, { useState, useMemo, useCallback } from 'react';
import { api } from './api';
import { recarregar } from './dados';
import { useConcluidas } from './concluidasStore';
import { PageHeader, Card, Campo, Botao, Busca, Aviso, Vazio, inputCls, normalizar, IconCubo, IconMais, IconLapis, IconLixo } from './ui';

const VAZIO = { idFirebase: '', nrModuloSequencial: '', descricao: '', ativo: true };

const ModuloCadastro = ({ modulos, tasks = [], currentUser, setCurrentView }) => {
    const [formData, setFormData] = useState(VAZIO);
    const [descricaoOriginal, setDescricaoOriginal] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [aviso, setAviso] = useState(null);
    const [busca, setBusca] = useState('');
    const fecharAviso = useCallback(() => setAviso(null), []);
    const [ordem, setOrdem] = useState('nome');

    // Atendimentos por módulo:
    //  - FINALIZADOS: as pendências concluídas (mesma consulta da tela "Concluídas": status == 'Concluído')
    //  - EM ABERTO: as pendências do quadro (já carregadas pelo App)
    // Cada pendência é ligada ao módulo pelo ID e, nas antigas sem ID, pelo nome gravado.
    const { concluidas, carregando: carregandoConcluidas } = useConcluidas();

    const contarPorModulo = useCallback((lista) => {
        const porId = new Set(modulos.map(m => m.idFirebase));
        const porNome = {};
        modulos.forEach(m => { porNome[normalizar(m.descricao)] = m.idFirebase; });
        const cont = {};
        lista.forEach(t => {
            const id = (t.moduloId && porId.has(t.moduloId)) ? t.moduloId : porNome[normalizar(t.modulo)];
            if (id) cont[id] = (cont[id] || 0) + 1;
        });
        return cont;
    }, [modulos]);

    const finalizados = useMemo(() => contarPorModulo(concluidas), [concluidas, contarPorModulo]);
    const abertos = useMemo(() => contarPorModulo(tasks), [tasks, contarPorModulo]);

    const handleNewModulo = () => {
        setFormData(VAZIO);
        setDescricaoOriginal('');
        setIsEditing(false);
    };

    const handleEditModulo = (modulo) => {
        setFormData({ idFirebase: modulo.idFirebase, nrModuloSequencial: modulo.nrModuloSequencial || '', descricao: modulo.descricao || '', ativo: modulo.ativo !== false });
        setDescricaoOriginal(modulo.descricao || '');
        setIsEditing(true);
        setAviso(null);
    };

    const handleSaveModulo = async (e) => {
        e.preventDefault();
        const descricao = formData.descricao.trim();
        if (!descricao) {
            setAviso({ tipo: 'erro', texto: 'Informe a descrição do módulo.' });
            return;
        }
        if (modulos.some(m => m.idFirebase !== formData.idFirebase && normalizar(m.descricao) === normalizar(descricao))) {
            setAviso({ tipo: 'erro', texto: 'Já existe um módulo com esta descrição.' });
            return;
        }
        setLoading(true);
        try {
            if (isEditing) {
                // o servidor também atualiza o nome do módulo gravado nas pendências
                const r = await api.put(`/modulos/${formData.idFirebase}`, { descricao, ativo: formData.ativo });
                await recarregar('modulos');
                const qtd = r.descricao !== descricaoOriginal ? (r.pendenciasAtualizadas || 0) : 0;
                if (r.pendenciasAtualizadas) recarregar('pendencias');
                setAviso({ tipo: 'ok', texto: `Módulo ${r.descricao} alterado${r.ativo === false ? ' (inativo)' : ''}${qtd ? ` (e ${qtd} pendência(s) atualizada(s))` : ''}.` });
            } else {
                const r = await api.post('/modulos', { descricao, ativo: formData.ativo });
                await recarregar('modulos');
                setAviso({ tipo: 'ok', texto: `Módulo ${r.descricao} cadastrado.` });
            }
            handleNewModulo();
        } catch (err) {
            console.error("Erro ao salvar módulo:", err);
            setAviso({ tipo: 'erro', texto: err.message });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteModulo = async (modulo) => {
        setLoading(true);
        try {
            if (!window.confirm(`Excluir o módulo ${modulo.descricao}? Esta ação não pode ser desfeita.`)) return;
            // o servidor recusa se o módulo estiver em alguma pendência
            await api.del(`/modulos/${modulo.idFirebase}`);
            await recarregar('modulos');
            setAviso({ tipo: 'ok', texto: `Módulo ${modulo.descricao} excluído.` });
            if (formData.idFirebase === modulo.idFirebase) handleNewModulo();
        } catch (err) {
            console.error("Erro ao excluir módulo:", err);
            setAviso({ tipo: 'erro', texto: err.message });
        } finally {
            setLoading(false);
        }
    };

    const lista = useMemo(() => {
        const t = normalizar(busca);
        const l = modulos.filter(m => !t || normalizar(m.descricao).includes(t));
        const total = (m) => (finalizados[m.idFirebase] || 0) + (abertos[m.idFirebase] || 0);
        if (ordem === 'chamados') return [...l].sort((x, y) => total(y) - total(x));
        if (ordem === 'abertos') return [...l].sort((x, y) => (abertos[y.idFirebase] || 0) - (abertos[x.idFirebase] || 0));
        return [...l].sort((x, y) => String(x.descricao || '').localeCompare(String(y.descricao || '')));
    }, [modulos, busca, ordem, finalizados, abertos]);

    const totalFinalizados = Object.values(finalizados).reduce((a, v) => a + v, 0);
    const totalAbertos = Object.values(abertos).reduce((a, v) => a + v, 0);
    const carregandoTotais = carregandoConcluidas;
    const inativos = modulos.filter(m => m.ativo === false).length;

    return (
        <div className="space-y-4">
            <PageHeader icon={IconCubo} cor="violet" titulo="Módulos"
                subtitulo={`${modulos.length} módulo${modulos.length === 1 ? '' : 's'}${inativos ? ` (${inativos} inativo${inativos === 1 ? '' : 's'})` : ''} · ${carregandoTotais ? 'contando atendimentos...' : `${totalFinalizados} finalizado${totalFinalizados === 1 ? '' : 's'} e ${totalAbertos} em aberto`}`}>
                <Botao variante="primario" onClick={handleNewModulo}><IconMais /> Novo módulo</Botao>
            </PageHeader>

            <Aviso aviso={aviso} onFechar={fecharAviso} />

            <div className="grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-4 items-start">
                <Card className="lg:sticky lg:top-0" titulo={isEditing ? 'Editando módulo' : 'Novo módulo'}>
                    <form onSubmit={handleSaveModulo} className="space-y-3">
                        <Campo rotulo="Descrição" obrigatorio ajuda={isEditing ? 'Ao renomear, as pendências deste módulo também são atualizadas.' : undefined}>
                            <input type="text" value={formData.descricao}
                                onChange={(e) => setFormData(p => ({ ...p, descricao: e.target.value.toUpperCase() }))}
                                className={`${inputCls} uppercase`} autoComplete="off" />
                        </Campo>
                        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
                            <input type="checkbox" checked={formData.ativo}
                                onChange={(e) => setFormData(p => ({ ...p, ativo: e.target.checked }))}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                            <span>
                                Ativo
                                <span className="block text-xs text-gray-500">Desmarcado, o módulo não aparece para escolha no cadastro de pendências. As pendências que já usam o módulo não mudam.</span>
                            </span>
                        </label>
                        <div className="flex gap-2 pt-1">
                            <Botao type="submit" variante="primario" disabled={loading} className="flex-1">
                                {loading ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Cadastrar'}
                            </Botao>
                            {isEditing && <Botao onClick={handleNewModulo} disabled={loading}>Cancelar</Botao>}
                        </div>
                        {isEditing && (
                            <button type="button" onClick={() => handleDeleteModulo(formData)} disabled={loading}
                                className="w-full text-xs text-red-600 hover:text-red-800 pt-1">
                                Excluir este módulo
                            </button>
                        )}
                    </form>
                </Card>

                <Card
                    semPadding
                    titulo={`Cadastrados (${lista.length})`}
                    acao={
                        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                            <select value={ordem} onChange={(e) => setOrdem(e.target.value)} className={`${inputCls} sm:w-48`} title="Ordenar">
                                <option value="nome">Ordem alfabética</option>
                                <option value="chamados">Mais atendimentos</option>
                                <option value="abertos">Mais em aberto</option>
                            </select>
                            <Busca valor={busca} onChange={setBusca} placeholder="Buscar módulo" className="flex-1 sm:w-56" />
                        </div>
                    }
                >
                    {lista.length === 0 ? (
                        <Vazio>{modulos.length ? 'Nenhum módulo encontrado.' : 'Nenhum módulo cadastrado.'}</Vazio>
                    ) : (
                        <div className="grid gap-3 p-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(15rem, 1fr))' }}>
                            {lista.map((m) => {
                                const ativo = m.idFirebase === formData.idFirebase;
                                const fin = finalizados[m.idFirebase] || 0;
                                const abe = abertos[m.idFirebase] || 0;
                                return (
                                    <div key={m.idFirebase} onClick={() => handleEditModulo(m)}
                                        className={`group relative rounded-lg border p-3 cursor-pointer transition-all ${ativo ? 'border-indigo-400 bg-indigo-50 shadow-sm' : 'border-gray-200 hover:border-indigo-300 hover:shadow-sm'} ${m.ativo === false ? 'opacity-60' : ''}`}>
                                        {/* ações aparecem ao passar o mouse, sem ocupar espaço do nome */}
                                        <div className="absolute top-1.5 right-1.5 flex bg-white/90 rounded-md opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                            <Botao tamanho="sm" variante="fantasma" title="Editar" onClick={() => handleEditModulo(m)}><IconLapis /></Botao>
                                            <Botao tamanho="sm" variante="fantasma" title="Excluir" className="text-red-500 hover:bg-red-50" onClick={() => handleDeleteModulo(m)}><IconLixo /></Botao>
                                        </div>

                                        <div className="flex items-center gap-2.5 pr-14">
                                            <span className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-violet-100 text-violet-700 flex-shrink-0"><IconCubo /></span>
                                            <div className="min-w-0">
                                                <div className="text-base font-bold text-gray-800 leading-tight break-words">{m.descricao}</div>
                                                {m.ativo === false && <span className="inline-block mt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600 bg-gray-200 rounded px-1.5 py-0.5">Inativo</span>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 mt-3">
                                            <div className="rounded-md bg-green-50 border border-green-100 px-2.5 py-2">
                                                <div className="text-3xl font-extrabold text-green-700 leading-none">
                                                    {carregandoTotais ? <span className="text-xl text-green-300">…</span> : fin}
                                                </div>
                                                <div className="text-xs font-semibold text-green-800 mt-1">Finalizados</div>
                                            </div>
                                            <div className={`rounded-md border px-2.5 py-2 ${abe ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'}`}>
                                                <div className={`text-3xl font-extrabold leading-none ${abe ? 'text-amber-600' : 'text-gray-400'}`}>{abe}</div>
                                                <div className={`text-xs font-semibold mt-1 ${abe ? 'text-amber-800' : 'text-gray-500'}`}>Em aberto</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default ModuloCadastro;
